import { NavLink } from "react-router-dom";
import {
  LogOut,
  Menu,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { PortalNavItem } from "@/lib/portalNavigation";
import bigLogo from "@/assets/big-logo.png";

export function PortalNavList({
  items,
  onNavigate,
}: {
  items: PortalNavItem[];
  onNavigate?: () => void;
}) {
  return (
    <nav aria-label="Client Portal navigation" className="space-y-1">
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/org"}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                "group flex min-h-10 items-center gap-3 rounded-lg px-3 py-2 font-heading text-sm font-bold transition-colors",
                isActive
                  ? "bg-[#174f39] text-white shadow-[0_6px_18px_rgba(23,79,57,0.16)]"
                  : "text-[#536159] hover:bg-[#eef4ef] hover:text-[#174f39]"
              )
            }
          >
            <Icon className="h-4 w-4 shrink-0" />
            {item.label}
          </NavLink>
        );
      })}
    </nav>
  );
}

function PortalIdentity({
  organizationName,
  organizationLogo,
  roleLabel,
}: {
  organizationName: string;
  organizationLogo?: string | null;
  roleLabel: string;
}) {
  return (
    <div className="border-b border-[#e0e5df] px-5 py-5">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#d9e2db] bg-[#f7f3eb]">
          <img src={organizationLogo || bigLogo} alt={organizationLogo ? organizationName : "BiG"} className="max-h-7 max-w-8 object-contain" />
        </div>
        <div className="min-w-0">
          <p className="font-body text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#277454]">Client Portal</p>
          <p className="truncate font-heading text-lg font-bold text-[#17221c]">{organizationName}</p>
        </div>
      </div>
      <p className="mt-3 font-body text-xs font-semibold text-[#647068]">{roleLabel}</p>
    </div>
  );
}

function PortalUserFooter({
  name,
  email,
  versionLabel,
  onSignOut,
}: {
  name?: string;
  email?: string;
  versionLabel?: string;
  onSignOut: () => void;
}) {
  return (
    <div className="border-t border-[#e0e5df] px-4 py-4">
      <div className="mb-3 min-w-0 px-1">
        {name ? <p className="truncate font-heading text-sm font-bold text-[#17221c]">{name}</p> : null}
        <p className="truncate font-body text-xs text-[#647068]">{email}</p>
      </div>
      <button type="button" onClick={onSignOut} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left font-body text-sm font-semibold text-[#536159] transition-colors hover:bg-[#eef4ef] hover:text-[#174f39]">
        <LogOut className="h-4 w-4" /> Sign out
      </button>
      {versionLabel ? <p className="mt-2 px-1 font-body text-[10px] text-[#8a938d]">{versionLabel}</p> : null}
    </div>
  );
}

export interface OrganizationPortalNavigationViewProps {
  organizationName: string;
  organizationLogo?: string | null;
  roleLabel: string;
  userName?: string;
  userEmail?: string;
  versionLabel?: string;
  items: PortalNavItem[];
  mobileOpen: boolean;
  onMobileToggle: () => void;
  onSignOut: () => void;
  previewMobile?: boolean;
}

export function OrganizationPortalNavigationView({
  organizationName,
  organizationLogo,
  roleLabel,
  userName,
  userEmail,
  versionLabel,
  items,
  mobileOpen,
  onMobileToggle,
  onSignOut,
  previewMobile = false,
}: OrganizationPortalNavigationViewProps) {
  return (
    <>
      <aside className={previewMobile ? "hidden" : "hidden h-full w-64 shrink-0 flex-col border-r border-[#d9ded8] bg-[#fffefb] lg:flex"}>
        <PortalIdentity organizationName={organizationName} organizationLogo={organizationLogo} roleLabel={roleLabel} />
        <div className="flex-1 overflow-y-auto px-3 py-5"><PortalNavList items={items} /></div>
        <PortalUserFooter name={userName} email={userEmail} versionLabel={versionLabel} onSignOut={onSignOut} />
      </aside>

      <div className={previewMobile ? "w-full max-w-sm border border-[#d9ded8] bg-[#fffefb]" : "border-b border-[#d9ded8] bg-[#fffefb] lg:hidden"}>
        <div className="flex min-h-16 items-center justify-between gap-3 px-4">
          <div className="min-w-0">
            <p className="font-body text-[9px] font-extrabold uppercase tracking-[0.15em] text-[#277454]">Client Portal</p>
            <p className="truncate font-heading text-base font-bold text-[#17221c]">{organizationName}</p>
          </div>
          <button type="button" aria-expanded={mobileOpen} aria-controls="client-portal-mobile-navigation" aria-label={mobileOpen ? "Close navigation" : "Open navigation"} onClick={onMobileToggle} className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#ccd5ce] text-[#174f39]">
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        {mobileOpen ? (
          <div id="client-portal-mobile-navigation" className="border-t border-[#e0e5df] px-4 py-4">
            <p className="mb-3 font-body text-xs font-semibold text-[#647068]">{roleLabel}</p>
            <PortalNavList items={items} onNavigate={onMobileToggle} />
            <div className="mt-4 border-t border-[#e0e5df] pt-3">
              <PortalUserFooter name={userName} email={userEmail} versionLabel={versionLabel} onSignOut={onSignOut} />
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}
